import { toasterApi } from '@terra/ui-design-system';
import { useTranslations } from 'next-intl';
import { useState } from 'react';
import { resolveApplicationStartUrl } from '@terra/shared-asset-processing';
import { useProxyStore } from '../../store/proxy-store';
import {
  useSelectedTarget,
  useSelectedCompany,
} from '../../store/projectStore';
import {
  useApplicationCredentials,
  type ApplicationCredential,
} from '../../hooks/useApplicationCredentials';
import { useElectronAPI } from '../../hooks/useElectronAPI';
import { useCachedCredentials } from '../../hooks/useCachedCredentials';
import { shouldLaunchWithFreshSession } from './browserLaunchSession';
import { buildBrowserInjectionPlan } from './browserLaunchReadiness';
import type {
  MtlsEntry,
  BrowserClientCertificate,
} from '../../electron/proxy-types';
import { registerTorchTrackingBrowserIfApplicable } from '../../utils/registerTorchTrackingBrowser';
import { normalizeOriginKey } from './normalizeOriginKey';

export function useLaunchBrowser() {
  const tBrowser = useTranslations('proxy.browser');
  const isProxyRunning = useProxyStore((state) => state.isProxyRunning);
  const settings = useProxyStore((state) => state.settings);
  const selectedTarget = useSelectedTarget();
  const selectedCompany = useSelectedCompany();
  const { launchChrome, injectCredentials, bindInstanceMtls, setMtlsConfig } =
    useElectronAPI();
  const [isLaunching, setIsLaunching] = useState(false);
  const { credentials, isLoading: isLoadingCredentials } =
    useApplicationCredentials({
      applicationId: selectedTarget?.id,
      enabled: !!selectedTarget,
    });
  const { credentials: cachedCredentials } = useCachedCredentials({
    applicationId: selectedTarget?.id,
    enabled: !!selectedTarget,
  });

  const launchBrowser = async (
    credential?: ApplicationCredential,
    options?: {
      excludeSettingsCerts?: boolean;
      stealthMode?: boolean;
    },
  ): Promise<void> => {
    if (!isProxyRunning) {
      toasterApi.error({
        title: 'Proxy Not Running',
        description: 'Start proxy to launch browser',
      });

      return;
    }

    setIsLaunching(true);

    try {
      const scopedApplicationUrl = selectedTarget
        ? resolveApplicationStartUrl({
            applicationUrl: selectedTarget.url,
            domains: selectedTarget.domains ?? [],
          })
        : undefined;
      const credentialLoginUrlRaw = credential?.loginUrl?.trim();
      const hasCredentialLoginUrl = Boolean(credentialLoginUrlRaw);
      let resolvedCredentialLoginUrl: string | undefined;

      if (credentialLoginUrlRaw) {
        try {
          resolvedCredentialLoginUrl = new URL(
            credentialLoginUrlRaw,
            scopedApplicationUrl ?? selectedTarget?.url,
          ).toString();
        } catch {
          resolvedCredentialLoginUrl = undefined;
        }
      }

      if (hasCredentialLoginUrl && !resolvedCredentialLoginUrl) {
        toasterApi.warning({
          title: 'Invalid Credential Login URL',
          description:
            'Using application URL because the credential login URL is invalid or cannot be resolved.',
        });
      }

      const credentialTargetUrl = hasCredentialLoginUrl
        ? resolvedCredentialLoginUrl || scopedApplicationUrl
        : undefined;
      const launchTargetUrl =
        credential && credentialTargetUrl !== undefined
          ? credentialTargetUrl
          : credential
            ? undefined
            : scopedApplicationUrl;
      const excludeSettingsCerts = !!options?.excludeSettingsCerts;
      const isStealth = !!options?.stealthMode;
      const credentialMtlsOrigin =
        credential?.mtlsOrigin?.trim() ||
        scopedApplicationUrl ||
        selectedTarget?.url ||
        undefined;
      const hasCredentialMtls = Boolean(
        credentialMtlsOrigin &&
          (credential?.mtlsPfx ||
            (credential?.mtlsCert && credential?.mtlsKey)),
      );
      const enabledSettingsEntries = settings.clientTlsCertificates.filter(
        (entry) => entry.enabled,
      );
      const settingsClientCertificates: BrowserClientCertificate[] =
        excludeSettingsCerts
          ? []
          : enabledSettingsEntries.map((entry) => ({
              origin: entry.origin,
              cert: entry.cert,
              key: entry.key,
              pfx: entry.pfx,
              passphrase: entry.passphrase,
            }));
      const credentialClientCertificate: BrowserClientCertificate | undefined =
        hasCredentialMtls && credentialMtlsOrigin
          ? {
              origin: credentialMtlsOrigin,
              cert: credential?.mtlsCert,
              key: credential?.mtlsKey,
              pfx: credential?.mtlsPfx,
              passphrase: credential?.mtlsPassphrase,
            }
          : undefined;
      // The credential cert wins for its own origin, but settings entries for
      // *other* origins still need to be sent to the browser launch.
      const credentialOriginKey = credentialClientCertificate
        ? normalizeOriginKey(credentialClientCertificate.origin)
        : undefined;
      const clientCertificates: BrowserClientCertificate[] =
        credentialClientCertificate
          ? [
              credentialClientCertificate,
              ...settingsClientCertificates.filter(
                (entry) =>
                  normalizeOriginKey(entry.origin) !== credentialOriginKey,
              ),
            ]
          : settingsClientCertificates;
      const mtlsSyncResult = await setMtlsConfig(
        excludeSettingsCerts
          ? []
          : enabledSettingsEntries.map((entry) => ({
              id: entry.id,
              origin: entry.origin,
              cert: entry.cert ?? null,
              key: entry.key ?? null,
              pfx: entry.pfx ?? null,
              passphrase: entry.passphrase ?? null,
              source: entry.source,
              enabled: true,
            })),
      );

      if (!mtlsSyncResult.success) {
        throw new Error(
          mtlsSyncResult.error || 'Failed to sync mTLS settings to proxy',
        );
      }

      const result = await launchChrome({
        proxyHost: settings.host,
        proxyPort: settings.port,
        freshSession: shouldLaunchWithFreshSession(),
        startUrl: launchTargetUrl,
        stealthMode: isStealth,
        tenantId: selectedCompany?.tenantId ?? undefined,
        applicationId: selectedTarget?.id,
        extensionsEnabled: settings.extensionsEnabled,
        clientCertificates,
      });

      if (!result.success) {
        throw new Error(result.error || 'Failed to launch browser');
      }

      // Type assertion for browser launch response which includes instanceId and message
      const launchResult = result as {
        success: boolean;
        instanceId?: string;
        focusedExisting?: boolean;
        message?: string;
      };
      const browserId = launchResult.instanceId;
      const isFocused = launchResult.focusedExisting === true;

      registerTorchTrackingBrowserIfApplicable({
        browserId,
        applicationId: selectedTarget?.id,
      });
      // For "no client TLS" launches, restore settings-configured certs to the
      // proxy registry once the browser instance closes. Until then, settings
      // certs are intentionally cleared from the (global) proxy registry, which
      // also affects any other concurrent browser sessions.
      if (
        excludeSettingsCerts &&
        browserId &&
        !isFocused &&
        window.electronAPI?.browser?.onInstanceClosed
      ) {
        const unsubscribe = window.electronAPI.browser.onInstanceClosed(
          (data) => {
            if (data.browserId !== browserId) {
              return;
            }

            unsubscribe();
            const currentEntries = useProxyStore
              .getState()
              .settings.clientTlsCertificates.filter((entry) => entry.enabled)
              .map((entry) => ({
                id: entry.id,
                origin: entry.origin,
                cert: entry.cert ?? null,
                key: entry.key ?? null,
                pfx: entry.pfx ?? null,
                passphrase: entry.passphrase ?? null,
                source: entry.source,
                enabled: true,
              }));

            void setMtlsConfig(currentEntries);
          },
        );
      }

      // If a credential was selected and browser launched successfully, inject credentials
      if (credential && browserId && !isFocused) {
        if (hasCredentialMtls && credentialMtlsOrigin) {
          const entry: MtlsEntry = {
            id: credential.id,
            origin: credentialMtlsOrigin,
            cert: credential.mtlsCert ?? null,
            key: credential.mtlsKey ?? null,
            pfx: credential.mtlsPfx ?? null,
            passphrase: credential.mtlsPassphrase ?? null,
            source: 'instance',
            enabled: true,
          };

          await bindInstanceMtls({ instanceId: browserId, entry });
        }

        const selectedCachedCredential = cachedCredentials.find(
          (cachedCredential) => cachedCredential.id === credential.id,
        );
        const injectionPlan = buildBrowserInjectionPlan({
          credential,
          cached: selectedCachedCredential,
          applicationUrl: scopedApplicationUrl ?? selectedTarget?.url,
          credentialTargetUrl,
        });
        const injectResult = await injectCredentials({
          browserId,
          targetUrl: injectionPlan.targetUrl,
          credentials: injectionPlan.credentials,
        });

        if (injectResult.success) {
          toasterApi.success({
            title: 'Browser Launched with Credentials',
            description: injectionPlan.successToastDescription,
          });
        } else if (injectResult.landedOnLogin) {
          toasterApi.warning({
            title: 'Landed on Login Page',
            description:
              injectResult.error ??
              'Re-verify this credential in target settings, then launch again.',
          });
        } else {
          toasterApi.error({
            title: 'Credential Injection Failed',
            description: `Browser launched but failed to inject credentials: ${injectResult.error}`,
          });
        }
      } else if (isFocused) {
        toasterApi.success({
          title: 'Browser Focused',
          description: 'Brought existing browser window to front',
        });
      } else if (excludeSettingsCerts) {
        toasterApi.success({
          title: 'Browser Launched (no client TLS)',
          description:
            'Settings-configured client TLS certificates are disabled until this browser closes',
        });
      } else if (isStealth) {
        toasterApi.success({
          title: tBrowser('stealthLaunchedTitle'),
          description: tBrowser('stealthLaunchedDescription'),
        });
      } else {
        toasterApi.success({
          title: 'Browser Launched',
          description: 'Browser started with proxy configuration',
        });
      }
    } catch (error) {
      console.error('Failed to launch browser:', error);
      toasterApi.error({
        title: 'Launch Failed',
        description: error instanceof Error ? error.message : 'Unknown error',
      });
    } finally {
      setIsLaunching(false);
    }
  };

  return {
    launchBrowser,
    isLaunching,
    isProxyRunning,
    hasEnabledSettingsMtls: settings.clientTlsCertificates.some(
      (entry) => entry.enabled,
    ),
    credentials,
    cachedCredentials,
    isLoadingCredentials,
  };
}
